import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DeleteLoreEntryDialog } from "@/components/lore/delete-lore-entry-dialog";
import { LoreDocumentRail } from "@/components/lore/lore-document-rail";
import { LoreEntryForm } from "@/components/lore/lore-entry-form";
import { ProjectDocumentEditor } from "@/components/lore/project-document-editor";
import { ImageGenerationWorkflowDialogs } from "@/components/image-generation/image-generation-workflow-dialogs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { applyLoreFilters } from "@/features/lore";
import {
  buildImageDraftSessionFromSource,
  buildLoreImagePrompt,
  isLoreEntryImageGenerationEligible,
  mapImageCitation,
} from "@/features/image-generation";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import { acceptStoryImportElement } from "@/features/story-import/api/storyImportClient";
import { isStoryImportReviewEditState } from "@/features/story-import/review/importReviewEdit";
import { useLoreWorkspaceStore } from "@/stores/loreWorkspaceStore";
import { useProjectImageStore } from "@/stores/project-image-store";
import { projectShellStore } from "@/stores/projectShellStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import type { ProjectDocumentKind } from "@/types/lore";
import {
  buildImportedLoreElementData,
  buildImportedLoreEntryDraft,
  buildImportedProjectDocumentElementData,
  buildImportedProjectDocumentView,
} from "@/features/lore/api/loreClient";

export function LoreWorkspacePage() {
  const { t } = useTranslation();
  const imageCreditCost = useRuntimeCreditCost("image_generation");
  const location = useLocation();
  const navigate = useNavigate();
  const { loreEntryId, projectId } = useParams<{ loreEntryId?: string; projectId: string }>();
  const importReviewEditState = useMemo(() => {
    if (
      !projectId
      || !isStoryImportReviewEditState(location.state)
      || location.state.projectId !== projectId
    ) {
      return null;
    }

    return location.state;
  }, [location.state, projectId]);
  const activeDocumentKind = useLoreWorkspaceStore((state) => state.activeDocumentKind);
  const activeEntryId = useLoreWorkspaceStore((state) => state.activeEntryId);
  const applyGeneratedCover = useLoreWorkspaceStore((state) => state.applyGeneratedCover);
  const capacity = useLoreWorkspaceStore((state) => state.capacity);
  const clearLimitPrompt = useLoreWorkspaceStore((state) => state.clearLimitPrompt);
  const clearNotice = useLoreWorkspaceStore((state) => state.clearNotice);
  const closeDeleteDialog = useLoreWorkspaceStore((state) => state.closeDeleteDialog);
  const createLoreEntry = useLoreWorkspaceStore((state) => state.createLoreEntry);
  const deleteDialog = useLoreWorkspaceStore((state) => state.deleteDialog);
  const deleteLoreEntry = useLoreWorkspaceStore((state) => state.deleteLoreEntry);
  const documentMap = useLoreWorkspaceStore((state) => state.documents);
  const filters = useLoreWorkspaceStore((state) => state.filters);
  const hydrateWorkspace = useLoreWorkspaceStore((state) => state.hydrateWorkspace);
  const isCreatingEntry = useLoreWorkspaceStore((state) => state.isCreatingEntry);
  const limitPrompt = useLoreWorkspaceStore((state) => state.limitPrompt);
  const loreEntries = useLoreWorkspaceStore((state) => state.loreEntries);
  const notice = useLoreWorkspaceStore((state) => state.notice);
  const openDeleteDialog = useLoreWorkspaceStore((state) => state.openDeleteDialog);
  const openDocument = useLoreWorkspaceStore((state) => state.openDocument);
  const openEntry = useLoreWorkspaceStore((state) => state.openEntry);
  const resetFilters = useLoreWorkspaceStore((state) => state.resetFilters);
  const saveLoreEntry = useLoreWorkspaceStore((state) => state.saveLoreEntry);
  const saveProjectDocument = useLoreWorkspaceStore((state) => state.saveProjectDocument);
  const setSearchText = useLoreWorkspaceStore((state) => state.setSearchText);
  const setStatusFilter = useLoreWorkspaceStore((state) => state.setStatusFilter);
  const setTypeFilter = useLoreWorkspaceStore((state) => state.setTypeFilter);
  const startCreateEntry = useLoreWorkspaceStore((state) => state.startCreateEntry);
  const status = useLoreWorkspaceStore((state) => state.status);
  const updateLoreEntryCanonStatus = useLoreWorkspaceStore(
    (state) => state.updateLoreEntryCanonStatus,
  );
  const startDraftSession = useProjectImageStore((state) => state.startDraftSession);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const tier = useSubscriptionStore((state) => state.tier);
  const documents = useMemo(
    () =>
      (["governance", "story-spine"] as ProjectDocumentKind[])
        .map((kind) => documentMap[kind])
        .filter((document): document is NonNullable<typeof document> => Boolean(document)),
    [documentMap],
  );
  const activeDocument = useMemo(
    () => (activeDocumentKind ? documentMap[activeDocumentKind] : null),
    [activeDocumentKind, documentMap],
  );
  const activeEntry = useMemo(
    () => loreEntries.find((entry) => entry.id === activeEntryId) ?? null,
    [activeEntryId, loreEntries],
  );
  const visibleEntries = useMemo(
    () => applyLoreFilters(loreEntries, filters),
    [filters, loreEntries],
  );
  const importedLoreDraft = useMemo(
    () => {
      if (
        !importReviewEditState
        || (importReviewEditState.element.elementType !== "location"
          && importReviewEditState.element.elementType !== "world_system")
      ) {
        return null;
      }

      return buildImportedLoreEntryDraft({
        displayTitle: importReviewEditState.element.displayTitle,
        elementData: importReviewEditState.element.elementData,
        elementType: importReviewEditState.element.elementType,
      });
    },
    [importReviewEditState],
  );
  const importedDocument = useMemo(() => {
    if (
      !importReviewEditState
      || (importReviewEditState.element.elementType !== "governance"
        && importReviewEditState.element.elementType !== "story_spine")
    ) {
      return null;
    }

    return buildImportedProjectDocumentView(
      importReviewEditState.element.elementType === "governance" ? "governance" : "story-spine",
      importReviewEditState.element.elementData.body && typeof importReviewEditState.element.elementData.body === "object"
        ? importReviewEditState.element.elementData.body as Parameters<typeof buildImportedProjectDocumentView>[1]
        : {
            content: [{
              content: [{
                text: String(importReviewEditState.element.elementData.summary ?? importReviewEditState.element.previewText),
                type: "text",
              }],
              type: "paragraph",
            }],
            type: "doc",
          },
    );
  }, [importReviewEditState]);

  async function handleGenerateLoreImage() {
    if (!projectId || !activeEntry || !isLoreEntryImageGenerationEligible(activeEntry)) {
      return;
    }

    const projectImageSettings = await projectShellStore
      .getState()
      .hydrateProjectImageSettings(projectId);

    if (!projectImageSettings) {
      return;
    }

    startDraftSession(
      buildImageDraftSessionFromSource({
        creditCost: imageCreditCost,
        creditsRemaining: creditBalance,
        existingImagePath: activeEntry.coverImagePath,
        projectId,
        projectImageSettings,
        prompt: buildLoreImagePrompt({
          body: activeEntry.mainBody,
          styleDescription: projectImageSettings.styleDescription,
          title: activeEntry.title,
          typeLabel: t(activeEntry.typeLabelKey),
        }),
        sourceCitations: [
          mapImageCitation({
            canonStatusLabel: t(activeEntry.canonStatusLabelKey),
            reason: "Selected lore entry for this prompt.",
            sourceKind: "lore_entry",
            sourceLabel: activeEntry.title,
            sourceRecordId: activeEntry.id,
          }),
        ],
        sourceId: activeEntry.id,
        sourceKind: "lore_entry",
        sourceTitle: activeEntry.title,
        tier,
      }),
    );
  }

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void hydrateWorkspace(projectId);
  }, [hydrateWorkspace, projectId]);

  useEffect(() => {
    if (!loreEntryId || status !== "ready") {
      return;
    }

    if (loreEntries.some((entry) => entry.id === loreEntryId)) {
      openEntry(loreEntryId);
    }
  }, [loreEntries, loreEntryId, openEntry, status]);

  if (!projectId) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("projects.dashboard.missing")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "loading" && documents.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("lore.workspace.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "error" && documents.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
              {t("lore.workspace.eyebrow")}
            </p>
            <h2 className="text-3xl text-foreground">{t("lore.workspace.unavailableTitle")}</h2>
          </div>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {notice ? t(notice.message) : t("lore.messages.loadError")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="project-shell-page">
      {notice ? (
        <div className="project-shell-notice" role={notice.kind === "error" ? "alert" : "status"}>
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {importReviewEditState ? (
        <div className="rounded-[1.75rem] border border-emerald-200/70 bg-emerald-50/90 px-5 py-4 text-sm text-emerald-950 shadow-vellum" role="status">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">{t("storyImport.actions.editBeforeAccept")}</p>
              <p>{t("storyImport.review.body")}</p>
            </div>
            <Button
              onClick={() => navigate(importReviewEditState.reviewRoute, { replace: true })}
              size="sm"
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.backToImport")}
            </Button>
          </div>
        </div>
      ) : null}

      <section
        className={
          importReviewEditState
            ? "grid gap-6"
            : "grid gap-8 xl:grid-cols-[20rem_minmax(0,1fr)] xl:items-start"
        }
      >
        {!importReviewEditState ? (
          <LoreDocumentRail
            activeDocumentKind={activeDocument?.kind ?? null}
            activeEntryId={activeEntry?.id ?? null}
            allowCreate={!importReviewEditState}
            capacity={capacity}
            documents={documents}
            entries={visibleEntries}
            filters={filters}
            isCreatingEntry={isCreatingEntry}
            limitPrompt={limitPrompt}
            onClearFilters={() => {
              clearLimitPrompt();
              resetFilters();
            }}
            onCreateEntry={() => {
              clearLimitPrompt();
              startCreateEntry();
            }}
            onOpenDocument={openDocument}
            onOpenEntry={openEntry}
            onSearchTextChange={setSearchText}
            onStatusFilterChange={setStatusFilter}
            onTypeFilterChange={setTypeFilter}
          />
        ) : null}

        <div className="min-w-0 space-y-8">
          {importedDocument ? (
            <ProjectDocumentEditor
              document={importedDocument}
              onSave={async (body) => {
                if (!importReviewEditState) {
                  return;
                }

                await acceptStoryImportElement({
                  editedElementData: buildImportedProjectDocumentElementData(
                    { body },
                    importReviewEditState.element.elementData,
                  ),
                  elementId: importReviewEditState.element.id,
                  jobId: importReviewEditState.importJobId,
                });
                navigate(importReviewEditState.reviewRoute, { replace: true });
              }}
            />
          ) : activeDocument ? (
            <ProjectDocumentEditor
              document={activeDocument}
              onSave={async (body) => {
                await saveProjectDocument(activeDocument.kind, { body });
              }}
            />
          ) : null}

          {importedLoreDraft ? (
            <LoreEntryForm
              entry={null}
              initialDraft={importedLoreDraft}
              mode="create"
              onCancelCreate={() => {
                if (importReviewEditState) {
                  navigate(importReviewEditState.reviewRoute, { replace: true });
                }
              }}
              onCreate={async (input) => {
                if (!importReviewEditState) {
                  throw new Error("Missing import review edit state.");
                }

                await acceptStoryImportElement({
                  editedElementData: buildImportedLoreElementData(
                    input,
                    importReviewEditState.element.elementData,
                  ),
                  elementId: importReviewEditState.element.id,
                  jobId: importReviewEditState.importJobId,
                });
                navigate(importReviewEditState.reviewRoute, { replace: true });
                return;
              }}
            />
          ) : isCreatingEntry ? (
            <LoreEntryForm
              createDraftStorageKey={projectId}
              entry={null}
              mode="create"
              onCancelCreate={() => openDocument("governance")}
              onCreate={createLoreEntry}
            />
          ) : null}

          {!isCreatingEntry && activeEntry ? (
            <LoreEntryForm
              entry={activeEntry}
              mode="edit"
              onAutosave={(input) => saveLoreEntry(activeEntry.id, input)}
              onChangeCanonStatus={(canonStatus) =>
                updateLoreEntryCanonStatus(activeEntry.id, canonStatus).then(() => undefined)}
              onDelete={() => openDeleteDialog(activeEntry.id)}
              onGenerateCover={handleGenerateLoreImage}
              onRemoveCover={(input) => saveLoreEntry(activeEntry.id, input).then(() => undefined)}
              onUploadCover={(input) => saveLoreEntry(activeEntry.id, input).then(() => undefined)}
            />
          ) : null}

          {!isCreatingEntry && !activeEntry && !activeDocument ? (
            <Card className="rounded-[2rem] border border-white/70 bg-white/70 shadow-vellum">
              <CardContent className="p-8">
                <p className="text-base text-muted-foreground">
                  {t("lore.workspace.emptySelection")}
                </p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>

      <DeleteLoreEntryDialog
        entryTitle={deleteDialog.target?.title ?? ""}
        onConfirm={() => void deleteLoreEntry()}
        onOpenChange={closeDeleteDialog}
        open={deleteDialog.open}
        submitting={deleteDialog.submitting}
      />
      <ImageGenerationWorkflowDialogs
        onApproved={async (result) => {
          if (activeEntry && result.approvedAssetRef) {
            await applyGeneratedCover(
              activeEntry.id,
              result.approvedAssetRef,
              result.creditsRemaining ?? null,
            );
          }
        }}
      />
    </div>
  );
}
